<template>
  <div class="pronunciation-evaluator">
    <div class="evaluator-container">
      <h2 class="title">口语评测测试</h2>
      
      <!-- 输入区域 -->
      <div class="input-section">
        <el-input
          v-model="inputText"
          placeholder="请输入要评测的单词或句子"
          class="text-input"
          :disabled="isRecording"
        />
      </div>

      <!-- 录音控制区域 -->
      <div class="recording-section">
        <el-button
          :type="isRecording ? 'danger' : 'primary'"
          :icon="isRecording ? 'el-icon-video-pause' : 'el-icon-microphone'"
          @click="handleRecordingBtnClick"
          :loading="isEvaluating"
          :disabled="!inputText || inputText.trim() === ''"
          size="large"
          class="record-btn"
        >
          {{ isRecording ? '停止录音' : '开始录音' }}
        </el-button>
        
        <div v-if="isRecording" class="recording-indicator">
          <span class="recording-dot"></span>
          <span>正在录音中...</span>
        </div>
      </div>

      <!-- 评测结果区域 -->
      <div v-if="evaluationResult" class="result-section">
        <h3 class="result-title">评测结果</h3>
        
        <!-- 总分 -->
        <div class="score-card">
          <div class="score-label">总分</div>
          <div class="score-value" :class="getScoreClass(evaluationResult.score)">
            {{ evaluationResult.score?.toFixed(2) || 'N/A' }}
          </div>
        </div>

        <!-- 详细评分 -->
        <div v-if="evaluationResult.lines && evaluationResult.lines.length > 0" class="detail-scores">
          <div class="detail-item">
            <span class="detail-label">流利度：</span>
            <span class="detail-value">{{ evaluationResult.lines[0].fluency?.toFixed(2) || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">完整度：</span>
            <span class="detail-value">{{ evaluationResult.lines[0].integrity?.toFixed(2) || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">标准度：</span>
            <span class="detail-value">{{ evaluationResult.lines[0].pronunciation?.toFixed(2) || 'N/A' }}</span>
          </div>
        </div>

        <!-- 单词级别评分 -->
        <div v-if="evaluationResult.lines && evaluationResult.lines[0].words" class="words-section">
          <h4 class="words-title">单词评分</h4>
          <div class="words-list">
            <div
              v-for="(word, index) in evaluationResult.lines[0].words"
              :key="index"
              class="word-item"
            >
              <span class="word-text">{{ word.text || word.sample }}</span>
              <span class="word-score" :class="getScoreClass(word.score)">
                {{ word.score?.toFixed(2) || 'N/A' }}
              </span>
            </div>
          </div>
        </div>

        <!-- 完整结果 JSON -->
        <el-collapse class="json-section">
          <el-collapse-item title="查看完整结果 (JSON)" name="1">
            <pre class="json-content">{{ JSON.stringify(evaluationResult, null, 2) }}</pre>
          </el-collapse-item>
        </el-collapse>
      </div>

      <!-- 错误提示 -->
      <div v-if="errorMessage" class="error-message">
        <el-alert
          :title="errorMessage"
          type="error"
          :closable="true"
          @close="errorMessage = ''"
        />
      </div>

      <!-- 音频播放 -->
      <div v-if="audioUrl" class="audio-section">
        <audio ref="audioPlayer" :src="audioUrl" controls></audio>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PronunciationEvaluator',
  data() {
    return {
      inputText: '',
      isRecording: false,
      isEvaluating: false,
      rec: null,
      audioChunks: [],
      audioUrl: null,
      evaluationResult: null,
      errorMessage: '',
      stream: null,
      // 云知声配置
      unisoundConfig: {
        appkey: 'd2vi6a6plyspmhigxtm2p2yqimjkyvxs5tyhnhy4@bfb79ecc0bb083a0d974e475e95983c4',
        deviceId: null,
        scoreCoefficient: 1.5,
        repeat: 0,
        mode: 'E',
        isChinese: false
      }
    }
  },
  beforeUnmount() {
    this.cleanup()
  },
  methods: {
    handleRecordingBtnClick() {
      if (!this.inputText || this.inputText.trim() === '') {
        this.$message.warning('请输入要评测的文本')
        return
      }

      if (this.isRecording) {
        this.handleStopRecording()
      } else {
        this.handleStartRecording()
      }
    },

    async handleStartRecording() {
      try {
        // 请求麦克风权限
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1
          }
        })

        // 初始化 MediaRecorder
        const options = { mimeType: 'audio/webm' }
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          options.mimeType = 'audio/webm;codecs=opus'
        }
        
        this.rec = new MediaRecorder(this.stream, options)
        this.audioChunks = []

        this.rec.ondataavailable = (e) => {
          if (e.data.size > 0) {
            this.audioChunks.push(e.data)
          }
        }

        this.rec.onstop = () => {
          this.processRecording()
        }

        this.rec.start()
        this.isRecording = true
        this.evaluationResult = null
        this.errorMessage = ''
      } catch (error) {
        console.error('启动录音失败:', error)
        this.$message.error('无法访问麦克风，请检查权限设置')
        this.errorMessage = '无法访问麦克风: ' + error.message
      }
    },

    handleStopRecording() {
      if (this.rec && this.rec.state !== 'inactive') {
        this.rec.stop()
      }
      this.isRecording = false
      this.cleanup()
    },

    async processRecording() {
      if (this.audioChunks.length === 0) {
        this.$message.warning('录音数据为空')
        return
      }

      try {
        this.isEvaluating = true
        
        // 创建音频 Blob
        const blob = new Blob(this.audioChunks, { type: 'audio/webm' })
        this.audioUrl = URL.createObjectURL(blob)

        // 转换为 WAV 格式
        const wavBlob = await this.convertToWav(blob)

        // 调用云知声评测接口
        await this.evaluatePronunciation(wavBlob, this.inputText.trim())
      } catch (error) {
        console.error('处理录音失败:', error)
        this.errorMessage = '评测失败: ' + (error.response?.data?.message || error.message)
        this.$message.error('评测失败，请重试')
      } finally {
        this.isEvaluating = false
      }
    },

    async convertToWav(webmBlob) {
      return new Promise((resolve, reject) => {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)()
        const fileReader = new FileReader()

        fileReader.onload = async (e) => {
          try {
            const audioBuffer = await audioContext.decodeAudioData(e.target.result)
            const wav = this.audioBufferToWav(audioBuffer)
            const wavBlob = new Blob([wav], { type: 'audio/wav' })
            resolve(wavBlob)
          } catch (err) {
            console.error('音频转换失败:', err)
            reject(err)
          }
        }

        fileReader.onerror = (err) => {
          reject(err)
        }

        fileReader.readAsArrayBuffer(webmBlob)
      })
    },

    audioBufferToWav(buffer) {
      const length = buffer.length
      const numberOfChannels = buffer.numberOfChannels
      const sampleRate = buffer.sampleRate
      const arrayBuffer = new ArrayBuffer(44 + length * numberOfChannels * 2)
      const view = new DataView(arrayBuffer)

      const writeString = (offset, string) => {
        for (let i = 0; i < string.length; i++) {
          view.setUint8(offset + i, string.charCodeAt(i))
        }
      }

      writeString(0, 'RIFF')
      view.setUint32(4, 36 + length * numberOfChannels * 2, true)
      writeString(8, 'WAVE')
      writeString(12, 'fmt ')
      view.setUint32(16, 16, true)
      view.setUint16(20, 1, true)
      view.setUint16(22, numberOfChannels, true)
      view.setUint32(24, sampleRate, true)
      view.setUint32(28, sampleRate * numberOfChannels * 2, true)
      view.setUint16(32, numberOfChannels * 2, true)
      view.setUint16(34, 16, true)
      writeString(36, 'data')
      view.setUint32(40, length * numberOfChannels * 2, true)

      let offset = 44
      for (let i = 0; i < length; i++) {
        for (let channel = 0; channel < numberOfChannels; channel++) {
          const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]))
          view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true)
          offset += 2
        }
      }

      return arrayBuffer
    },

    async evaluatePronunciation(audioBlob, text) {
      try {
        const response = await this.$store.dispatch('Recording/evaluatePronunciation', {
          audioBlob: audioBlob,
          text: text,
          audioFormat: 'wav',
          mode: this.unisoundConfig.mode,
          appkey: this.unisoundConfig.appkey,
          deviceId: this.unisoundConfig.deviceId,
          isChinese: this.unisoundConfig.isChinese,
          scoreCoefficient: this.unisoundConfig.scoreCoefficient,
          repeat: this.unisoundConfig.repeat
        })

        if (response && response.data) {
          this.evaluationResult = response.data
          this.$message.success('评测完成')
        } else {
          throw new Error('评测结果为空')
        }
      } catch (error) {
        console.error('评测失败:', error)
        throw error
      }
    },

    getScoreClass(score) {
      if (!score) return ''
      if (score >= 90) return 'score-excellent'
      if (score >= 80) return 'score-good'
      if (score >= 60) return 'score-fair'
      return 'score-poor'
    },

    cleanup() {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
      if (this.audioUrl) {
        URL.revokeObjectURL(this.audioUrl)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.pronunciation-evaluator {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.evaluator-container {
  background: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.title {
  margin: 0 0 30px 0;
  text-align: center;
  color: #303133;
  font-size: 24px;
  font-weight: 600;
}

.input-section {
  margin-bottom: 20px;
  
  .text-input {
    width: 100%;
  }
}

.recording-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 30px;
  
  .record-btn {
    min-width: 200px;
    height: 50px;
    font-size: 16px;
    margin-bottom: 10px;
  }
  
  .recording-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #f56c6c;
    font-size: 14px;
    
    .recording-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #f56c6c;
      animation: pulse 1.5s ease-in-out infinite;
    }
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.result-section {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid #ebeef5;
}

.result-title {
  margin: 0 0 20px 0;
  color: #303133;
  font-size: 20px;
  font-weight: 600;
}

.score-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  margin-bottom: 20px;
  color: white;
  
  .score-label {
    font-size: 14px;
    opacity: 0.9;
    margin-bottom: 8px;
  }
  
  .score-value {
    font-size: 48px;
    font-weight: bold;
    
    &.score-excellent {
      color: #67c23a;
    }
    &.score-good {
      color: #409eff;
    }
    &.score-fair {
      color: #e6a23c;
    }
    &.score-poor {
      color: #f56c6c;
    }
  }
}

.detail-scores {
  display: flex;
  justify-content: space-around;
  margin-bottom: 20px;
  padding: 15px;
  background: #f5f7fa;
  border-radius: 8px;
  
  .detail-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    
    .detail-label {
      font-size: 12px;
      color: #909399;
      margin-bottom: 5px;
    }
    
    .detail-value {
      font-size: 20px;
      font-weight: 600;
      color: #303133;
    }
  }
}

.words-section {
  margin-top: 20px;
  
  .words-title {
    margin: 0 0 15px 0;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }
  
  .words-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .word-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #f5f7fa;
    border-radius: 6px;
    
    .word-text {
      font-weight: 500;
      color: #303133;
    }
    
    .word-score {
      font-size: 14px;
      font-weight: 600;
      
      &.score-excellent {
        color: #67c23a;
      }
      &.score-good {
        color: #409eff;
      }
      &.score-fair {
        color: #e6a23c;
      }
      &.score-poor {
        color: #f56c6c;
      }
    }
  }
}

.json-section {
  margin-top: 20px;
  
  .json-content {
    background: #f5f7fa;
    padding: 15px;
    border-radius: 4px;
    overflow-x: auto;
    font-size: 12px;
    line-height: 1.5;
    color: #606266;
  }
}

.error-message {
  margin-top: 20px;
}

.audio-section {
  margin-top: 20px;
  
  audio {
    width: 100%;
  }
}
</style>

